import { HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest } from '@angular/common/http';
import { from, Observable, lastValueFrom } from 'rxjs';
import { AuthenticatedUser, DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import * as helperService from '@services/helper/helper.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { FeatureAccessMap } from '@airwallet/shared-models/feature_access';
import { getAuth, User } from '@angular/fire/auth';
import { ControlledDashboardOwnerUser } from '@airwallet/shared-models/controlled-dashboard-owner-user';
const unprotectedRoutes: string[] = ['/assets/i18n/', '/api_account/member/'];

export function headerInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return from(handleAccess(req, next));
}

async function handleAccess(originalRequest: HttpRequest<unknown>, next: HttpHandlerFn): Promise<HttpEvent<unknown>> {
    let modifiedRequest = originalRequest;
    // Check if the request URL matches any of the unprotected routes
    const isUnprotectedRoute = unprotectedRoutes.find(route => originalRequest.url.includes(route));

    // If the request URL matches an unprotected route, handle the request without the session token
    if (isUnprotectedRoute) {
        //Handle request without session token
        return lastValueFrom(next(originalRequest));
    } else {
        // Handle the request with the session token
        try {
            modifiedRequest = await addDefaultHeaders(modifiedRequest);
        } catch (error) {
            console.error('Error adding default headers', error);
            return lastValueFrom(next(originalRequest));
        }

        return handleAdditionalHeaders(modifiedRequest, next);
    }
}

function handleAdditionalHeaders(modifiedRequest: HttpRequest<unknown>, next: HttpHandlerFn): Promise<HttpEvent<unknown>> {
    const baseHeadersResponse = handleBaseHeadersOnly(modifiedRequest, next);
    if (baseHeadersResponse instanceof Promise) {
        return baseHeadersResponse;
    }

    modifiedRequest = handleControlledUser(modifiedRequest);
    const controlOnlyResponse = handleControlOnly(modifiedRequest, next);
    if (controlOnlyResponse instanceof Promise) {
        return controlOnlyResponse;
    }

    modifiedRequest = handleOwner(modifiedRequest);

    return lastValueFrom(next(modifiedRequest));
}

async function addDefaultHeaders(modifiedRequest: HttpRequest<unknown>): Promise<HttpRequest<unknown>> {
    try {
        const authToken: string = await fetchAuthToken();
        const accessMap: FeatureAccessMap = fetchAccessMap(modifiedRequest); // Assuming fetchAccessMap is a function that retrieves the accessmap
        if (authToken) {
            let headers: HttpHeaders = modifiedRequest.headers.append('Authorization', authToken).append('Content-Type', 'application/json');
            if (accessMap) {
                const accessmap: string = JSON.stringify(accessMap);
                headers = headers.append('accessmap', accessmap);
            }
            return modifiedRequest.clone({ headers });
        }
    } catch (error) {
        console.error('Error fetching auth state', error);
        throw error;
    }

    return modifiedRequest;
}

/**
 * Fetches the feature access map from local storage based on the request headers.
 * If the request has the 'X-AsAdmin' header and an admin feature access map exists in local storage,
 * it returns the admin feature access map. Otherwise, it returns the regular feature access map.
 * (This is needed when controlling a user as admin, but wanting to use the admin credentials to view e.g. Health Monitor)
 * @param modifiedRequest
 */
function fetchAccessMap(modifiedRequest): FeatureAccessMap | null {
    const localStorageService = new LocalStorageService();
    let map: FeatureAccessMap;
    const adminMap: FeatureAccessMap = localStorageService.getItem('admin_FeatureAccess');
    if (modifiedRequest.headers.has('X-AsAdmin') && adminMap) {
        map = adminMap;
    } else {
        map = localStorageService.getItem('featureAccess');
    }

    return map && Object.keys(map).length > 0 ? map : null;
}

async function fetchAuthToken(): Promise<string | null> {
    const user: User = getAuth().currentUser;
    if (!user) {
        return null;
    }
    const token: string = await user.getIdToken();
    if (!token) {
        if (!new helperService.HelperService().unguardedUrl(window.location.pathname)) {
            await this.signOut();
        }
    }
    return token;
}

function handleBaseHeadersOnly(modifiedRequest: HttpRequest<unknown>, next: HttpHandlerFn): Promise<HttpEvent<unknown>> | HttpRequest<unknown> {
    if (modifiedRequest.headers.has('X-Base-Headers-Only')) {
        modifiedRequest = modifiedRequest.clone({ headers: modifiedRequest.headers.delete('X-Base-Headers-Only') });
        return lastValueFrom(next(modifiedRequest));
    }
    return modifiedRequest;
}

function handleControlledUser(modifiedRequest: HttpRequest<unknown>): HttpRequest<unknown> {
    const controlledUser: ControlledDashboardOwnerUser = JSON.parse(localStorage.getItem('authenticatedUser'));
    if (controlledUser) {
        let controlleduid: string = controlledUser.uid.split('_operated_by_')[0];
        if (modifiedRequest.headers.has('X-AsAdmin')) {
            const adminUid: string = localStorage.getItem('admin_AuthenticatedUser') ? (JSON.parse(localStorage.getItem('admin_AuthenticatedUser')) as AuthenticatedUser).uid : null;
            if (adminUid) {
                controlleduid = adminUid; // Overwrite controlledUid with adminUid
            }
        }
        const newHeaders: HttpHeaders = modifiedRequest.headers.set('controlleduid', controlleduid);
        modifiedRequest = modifiedRequest.clone({ headers: newHeaders });
    }
    return modifiedRequest;
}

function handleControlOnly(modifiedRequest: HttpRequest<unknown>, next: HttpHandlerFn): Promise<HttpEvent<unknown>> | HttpRequest<unknown> {
    if (modifiedRequest.headers.has('X-Control-only')) {
        modifiedRequest = modifiedRequest.clone({ headers: modifiedRequest.headers.delete('X-Control-only') });
        return lastValueFrom(next(modifiedRequest));
    }
    return modifiedRequest;
}

function handleOwner(modifiedRequest: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!modifiedRequest.headers.has('X-Skip-Owner')) {
        const owner: string = localStorage.getItem('owner');
        if (owner) {
            const newHeaders: HttpHeaders = modifiedRequest.headers.append('owner', owner);
            modifiedRequest = modifiedRequest.clone({ headers: newHeaders });
        }
    } else {
        modifiedRequest = modifiedRequest.clone({ headers: modifiedRequest.headers.delete('X-Skip-Owner') });
    }
    return modifiedRequest;
}
